import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import MoreHorizOutlinedIcon from "@mui/icons-material/MoreHorizOutlined";

import { DashboardRegistryItem } from "./dashboardRegistry";

interface DashboardSwitcherProps {
  items: DashboardRegistryItem[];
  activeKey: string;
  onChange: (key: string) => void;
}

export default function DashboardSwitcher({
  items,
  activeKey,
  onChange,
}: DashboardSwitcherProps) {
  const pinnedItems = useMemo(() => items.filter((item) => item.pinned).slice(0, 10), [items]);
  const overflowItems = useMemo(
    () => items.filter((item) => !pinnedItems.some((p) => p.key === item.key)),
    [items, pinnedItems]
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  return (
    <Stack
      direction={{ xs: "column", md: "row" }}
      justifyContent="space-between"
      alignItems={{ xs: "flex-start", md: "center" }}
      spacing={1.5}
      sx={{ mt: 1.5, width: "100%", minWidth: 0, maxWidth: "100%" }}
    >
      <ToggleButtonGroup
        exclusive
        value={activeKey}
        onChange={(_, value) => value && onChange(value)}
        size="small"
        sx={{
          minWidth: 0,
          maxWidth: "100%",
          flexWrap: "wrap",
          gap: 1,
          "& .MuiToggleButton-root": {
            border: "1px solid #E2E8EE !important",
            borderRadius: "8px !important",
            px: 1.5,
            py: 0.75,
            fontWeight: 700,
            color: "#33495C",
          },
          "& .Mui-selected": {
            bgcolor: "#B84B00 !important",
            color: "#FFFFFF !important",
          },
        }}
      >
        {pinnedItems.map((item) => (
          <ToggleButton key={item.key} value={item.key}>
            {item.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      {overflowItems.length > 0 ? (
        <Box>
          <Button
            variant="outlined"
            startIcon={<MoreHorizOutlinedIcon />}
            onClick={(e) => setAnchorEl(e.currentTarget)}
          >
            More
          </Button>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
          >
            {overflowItems.map((item) => (
              <MenuItem
                key={item.key}
                selected={item.key === activeKey}
                onClick={() => {
                  onChange(item.key);
                  setAnchorEl(null);
                }}
              >
                <Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                    {item.label}
                  </Typography>
                  {item.description ? (
                    <Typography sx={{ fontSize: 12, color: "#66788A", mt: 0.25 }}>
                      {item.description}
                    </Typography>
                  ) : null}
                </Box>
              </MenuItem>
            ))}
          </Menu>
        </Box>
      ) : null}
    </Stack>
  );
}
